import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import poppinsFonts from "./fonts/poppins";

const registerFonts = (doc) => {
  doc.addFileToVFS("Poppins-Regular.ttf", poppinsFonts["Poppins-Regular"]);
  doc.addFont("Poppins-Regular.ttf", "Poppins", "normal");
  doc.addFileToVFS("Poppins-Bold.ttf", poppinsFonts["Poppins-Bold"]);
  doc.addFont("Poppins-Bold.ttf", "Poppins", "bold");
  doc.addFileToVFS("Poppins-Medium.ttf", poppinsFonts["Poppins-Medium"]);
  doc.addFont("Poppins-Medium.ttf", "PoppinsMedium", "normal");
  doc.addFileToVFS("Poppins-SemiBold.ttf", poppinsFonts["Poppins-SemiBold"]);
  doc.addFont("Poppins-SemiBold.ttf", "PoppinsSemiBold", "normal");
  doc.addFileToVFS("Poppins-Light.ttf", poppinsFonts["Poppins-Light"]);
  doc.addFont("Poppins-Light.ttf", "PoppinsLight", "normal");
};

export const generateInvoicePDF = (invoice, settings) => {
  const doc = new jsPDF({ orientation: "landscape", format: "a4", unit: "mm" });
  registerFonts(doc);

  const resourceName = invoice.resource_display_name || invoice.resource_name;
  const billTo = invoice.bill_to || settings?.company_name || "GRAPHIKPRO";
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 14;
  const cw = pw - m * 2;

  // ========== HEADER — white background, clean ==========

  // Thin top accent line (very subtle grey)
  doc.setFillColor(220, 220, 220);
  doc.rect(0, 0, pw, 0.8, "F");

  // Resource name — left, dark
  doc.setFont("Poppins", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 30);
  doc.text(resourceName, m, 16);

  // INVOICE — right, light grey
  doc.setFont("Poppins", "bold");
  doc.setFontSize(28);
  doc.setTextColor(180, 180, 180);
  doc.text("INVOICE", pw - m, 18, { align: "right" });

  // Invoice name
  if (invoice.invoice_name) {
    doc.setFont("Poppins", "normal");
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(invoice.invoice_name, m, 23);
  }

  // Light divider
  const divY = invoice.invoice_name ? 27 : 23;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.2);
  doc.line(m, divY, pw - m, divY);

  // ========== DETAILS — evenly spaced 4 columns ==========
  let dy = divY + 6;
  const colW = cw / 4;

  const drawField = (colIdx, label, value, bold, sz) => {
    const x = m + colIdx * colW;
    doc.setFont("Poppins", "normal");
    doc.setFontSize(7);
    doc.setTextColor(170, 170, 170);
    doc.text(label, x, dy);
    doc.setFont("Poppins", bold ? "bold" : "normal");
    doc.setFontSize(sz || 10);
    doc.setTextColor(50, 50, 50);
    doc.text(value || "", x, dy + 5.5);
  };

  drawField(0, "INVOICE NO.", invoice.invoice_number, true, 9);
  drawField(1, "DATE", invoice.invoice_date?.substring(0, 10) || "", false, 9);
  drawField(2, "BILL TO", billTo, true, 11);
  drawField(3, "FROM", resourceName.length > 32 ? resourceName.substring(0, 30) + "..." : resourceName, false, 9);

  // Another light divider
  doc.setDrawColor(235, 235, 235);
  doc.setLineWidth(0.2);
  doc.line(m, dy + 11, pw - m, dy + 11);

  // ========== TABLE — full width, centered, clean ==========
  const tableY = dy + 16;

  const tableData = invoice.line_items.map((item, idx) => [
    String(idx + 1),
    item.main_title || "-",
    item.episode_title || "-",
    item.episode_number != null ? String(item.episode_number) : "-",
    item.duration_words != null ? String(item.duration_words) : "-",
    item.rate_type === "flat"
      ? "Flat"
      : item.rate > 0
        ? "\u20B9" + item.rate.toFixed(2)
        : "-",
    item.amount > 0
      ? "\u20B9" + Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })
      : "-",
  ]);

  autoTable(doc, {
    startY: tableY,
    head: [["#", "Main Title", "Episode Title", "Ep #", "Dur / Words", "Rate", "Amount"]],
    body: tableData,
    theme: "plain",
    styles: {
      font: "Poppins",
      fontStyle: "normal",
      fontSize: 9,
      cellPadding: { top: 3.5, bottom: 3.5, left: 4, right: 4 },
      overflow: "linebreak",
      lineWidth: 0,
      halign: "center",
      valign: "middle",
      textColor: [60, 60, 60],
    },
    headStyles: {
      font: "PoppinsSemiBold",
      fontStyle: "normal",
      fillColor: [245, 245, 245],
      textColor: [100, 100, 100],
      fontSize: 8.5,
      cellPadding: { top: 5, bottom: 5, left: 4, right: 4 },
      halign: "center",
      lineWidth: { bottom: 0.3 },
      lineColor: [210, 210, 210],
    },
    bodyStyles: {
      textColor: [50, 50, 50],
      halign: "center",
      lineWidth: { bottom: 0.15 },
      lineColor: [240, 240, 240],
    },
    alternateRowStyles: {
      fillColor: [250, 250, 250],
    },
    columnStyles: {
      0: { cellWidth: 15, textColor: [170, 170, 170], fontSize: 8 },
      1: { cellWidth: (cw - 33) * 0.18 },
      2: { cellWidth: (cw - 33) * 0.30 },
      3: { cellWidth: 18 },
      4: { cellWidth: (cw - 33) * 0.12 },
      5: { cellWidth: (cw - 33) * 0.15 },
      6: { cellWidth: (cw - 33) * 0.25, font: "PoppinsSemiBold", fontStyle: "normal", textColor: [30, 30, 30] },
    },
    margin: { left: m, right: m },
    tableWidth: cw,
    showHead: "everyPage",
    didDrawPage: (data) => {
      // Continuation page header — minimal
      if (data.pageNumber > 1) {
        doc.setFillColor(245, 245, 245);
        doc.rect(0, 0, pw, 8, "F");
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.2);
        doc.line(0, 8, pw, 8);

        doc.setFont("PoppinsMedium", "normal");
        doc.setFontSize(8);
        doc.setTextColor(140, 140, 140);
        doc.text(invoice.invoice_number, m, 5.5);
        doc.text(resourceName, pw - m, 5.5, { align: "right" });
      }

      // Footer
      doc.setDrawColor(235, 235, 235);
      doc.setLineWidth(0.2);
      doc.line(m, ph - 9, pw - m, ph - 9);

      doc.setFont("Poppins", "normal");
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text(invoice.invoice_number, m, ph - 5);
      doc.text("Page " + data.pageNumber, pw - m, ph - 5, { align: "right" });
      doc.text("Thank you for your business", pw / 2, ph - 5, { align: "center" });
    },
  });

  // ========== TOTALS — right side, clean box ==========
  const fy = doc.lastAutoTable?.finalY || tableY;
  const bw = 105;
  const bx = pw - m - bw;
  let ty = fy + 8;

  if (ty + 48 > ph - 12) {
    doc.addPage();
    doc.setFillColor(245, 245, 245);
    doc.rect(0, 0, pw, 8, "F");
    doc.setDrawColor(230, 230, 230);
    doc.line(0, 8, pw, 8);
    ty = 16;
  }

  // Totals box — white with light grey border
  const bh = invoice.tds_amount > 0 ? 48 : 38;
  doc.setFillColor(250, 250, 250);
  doc.setDrawColor(225, 225, 225);
  doc.setLineWidth(0.3);
  doc.roundedRect(bx - 6, ty - 4, bw + 12, bh, 2, 2, "FD");

  // Subtotal
  doc.setFont("Poppins", "normal");
  doc.setFontSize(9);
  doc.setTextColor(130, 130, 130);
  doc.text("Subtotal", bx, ty + 4);
  doc.setFont("PoppinsMedium", "normal");
  doc.setTextColor(50, 50, 50);
  doc.text("\u20B9" + (invoice.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty + 4, { align: "right" });

  // TDS
  if (invoice.tds_amount && invoice.tds_amount > 0) {
    ty += 11;
    doc.setFont("Poppins", "normal");
    doc.setFontSize(9);
    doc.setTextColor(180, 80, 80);
    doc.text("TDS @ " + (invoice.tds_percentage || 1) + "%", bx, ty + 4);
    doc.text("- \u20B9" + invoice.tds_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty + 4, { align: "right" });
  }

  // Divider
  ty += 10;
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.4);
  doc.line(bx, ty + 1, pw - m, ty + 1);

  // Grand total
  ty += 10;
  doc.setFont("PoppinsSemiBold", "normal");
  doc.setFontSize(9);
  doc.setTextColor(130, 130, 130);
  doc.text("TOTAL DUE", bx, ty + 1);
  doc.setFont("Poppins", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 30);
  doc.text("\u20B9" + (invoice.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty + 1, { align: "right" });

  // Item count
  doc.setFont("Poppins", "normal");
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  doc.text((invoice.line_items?.length || 0) + " line items", m, fy + 10);

  // Return both blob and save
  const pdfBlob = doc.output("blob");
  doc.save("invoice_" + invoice.invoice_number + ".pdf");
  
  return pdfBlob;
};

// Generate PDF blob only (for upload without download)
export const generateInvoicePDFBlob = (invoice, settings) => {
  const doc = new jsPDF({ orientation: "landscape", format: "a4", unit: "mm" });
  registerFonts(doc);

  const resourceName = invoice.resource_display_name || invoice.resource_name;
  const billTo = invoice.bill_to || settings?.company_name || "GRAPHIKPRO";
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 14;
  const cw = pw - m * 2;

  // ========== HEADER — white background, clean ==========

  // Thin top accent line (very subtle grey)
  doc.setFillColor(220, 220, 220);
  doc.rect(0, 0, pw, 0.8, "F");

  // Resource name — left, dark
  doc.setFont("Poppins", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 30);
  doc.text(resourceName, m, 16);

  // INVOICE — right, light grey
  doc.setFont("Poppins", "bold");
  doc.setFontSize(28);
  doc.setTextColor(180, 180, 180);
  doc.text("INVOICE", pw - m, 18, { align: "right" });

  // Invoice name
  if (invoice.invoice_name) {
    doc.setFont("Poppins", "normal");
    doc.setFontSize(9);
    doc.setTextColor(140, 140, 140);
    doc.text(invoice.invoice_name, m, 23);
  }

  // Light divider
  const divY = invoice.invoice_name ? 27 : 23;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.2);
  doc.line(m, divY, pw - m, divY);

  // ========== TWO-COLUMN DETAILS ==========
  const detailsY = divY + 8;
  const leftColX = m;
  const rightColX = pw / 2 + 10;

  // Left column: Invoice details
  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text("INVOICE NO.", leftColX, detailsY);
  doc.setFont("PoppinsSemiBold", "normal");
  doc.setFontSize(10);
  doc.setTextColor(50, 50, 50);
  doc.text(invoice.invoice_number || "", leftColX, detailsY + 5);

  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text("DATE", leftColX, detailsY + 13);
  doc.setFont("Poppins", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text((invoice.invoice_date || "").substring(0, 10), leftColX, detailsY + 18);

  // Right column: Bill To / From
  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text("BILL TO", rightColX, detailsY);
  doc.setFont("PoppinsSemiBold", "normal");
  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  doc.text(billTo, rightColX, detailsY + 5);

  doc.setFont("Poppins", "normal");
  doc.setFontSize(7);
  doc.setTextColor(160, 160, 160);
  doc.text("FROM", rightColX, detailsY + 13);
  doc.setFont("Poppins", "normal");
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(resourceName, rightColX, detailsY + 18);

  // ========== TABLE ==========
  const tableStartY = detailsY + 28;
  const lineItems = invoice.line_items || [];

  const tableBody = lineItems.map((item, idx) => {
    const mainTitle = (item.main_title || "").substring(0, 34);
    const episodeTitle = (item.episode_title || "").substring(0, 30);
    const epNum = item.episode_number || "-";
    const durWords = item.duration_words !== null && item.duration_words !== undefined ? item.duration_words : "-";
    const rate = item.rate_type === "flat" ? "Flat" : item.rate > 0 ? item.rate.toFixed(2) : "-";
    const amount = item.amount > 0 ? "\u20B9" + item.amount.toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "-";
    return [idx + 1, mainTitle, episodeTitle, epNum, durWords, rate, amount];
  });

  autoTable(doc, {
    startY: tableStartY,
    head: [["#", "Main Title", "Episode Title", "Ep #", "Dur/Words", "Rate", "Amount"]],
    body: tableBody,
    margin: { left: m, right: m },
    styles: { font: "Poppins", fontSize: 8, cellPadding: 2.2, textColor: [60, 60, 60], lineColor: [230, 230, 230], lineWidth: 0.1 },
    headStyles: { fillColor: [245, 245, 245], textColor: [100, 100, 100], fontStyle: "bold", fontSize: 7.5 },
    columnStyles: {
      0: { halign: "center", cellWidth: 8, textColor: [150, 150, 150] },
      1: { cellWidth: 58 },
      2: { cellWidth: 52 },
      3: { halign: "center", cellWidth: 14 },
      4: { halign: "center", cellWidth: 22 },
      5: { halign: "right", cellWidth: 18 },
      6: { halign: "right", cellWidth: 28, fontStyle: "bold" },
    },
    alternateRowStyles: { fillColor: [252, 252, 252] },
    tableWidth: cw,
    showHead: "everyPage",
    didDrawPage: (data) => {
      const pn = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont("Poppins", "normal");
      doc.setFontSize(7);
      doc.setTextColor(180, 180, 180);
      doc.text("Page " + pn, pw - m, ph - 8, { align: "right" });
      doc.text(invoice.invoice_number || "", m, ph - 8);
    },
  });

  // ========== TOTALS ==========
  const fy = doc.lastAutoTable.finalY + 6;
  const bx = pw - m - 70;

  // Box background
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(bx - 4, fy - 2, 78, 38, 2, 2, "F");

  let ty = fy + 4;
  doc.setFont("Poppins", "normal");
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text("Subtotal", bx, ty);
  doc.setTextColor(60, 60, 60);
  doc.text("\u20B9" + (invoice.subtotal || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty, { align: "right" });

  if (invoice.tds_amount && invoice.tds_amount > 0) {
    ty += 7;
    doc.setTextColor(180, 80, 80);
    doc.text("TDS @ " + (invoice.tds_percentage || 1) + "%", bx, ty);
    doc.text("-\u20B9" + invoice.tds_amount.toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty, { align: "right" });
  }

  // Grand total
  ty += 10;
  doc.setFont("PoppinsSemiBold", "normal");
  doc.setFontSize(9);
  doc.setTextColor(130, 130, 130);
  doc.text("TOTAL DUE", bx, ty + 1);
  doc.setFont("Poppins", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 30, 30);
  doc.text("\u20B9" + (invoice.grand_total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 }), pw - m, ty + 1, { align: "right" });

  // Item count
  doc.setFont("Poppins", "normal");
  doc.setFontSize(8);
  doc.setTextColor(180, 180, 180);
  doc.text((invoice.line_items?.length || 0) + " line items", m, fy + 10);

  return doc.output("blob");
};
